import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Smile } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { api } from "../../lib/api";
import { useDesktop } from "../../lib/desktop";
import { EMOJIS, EMOJI_SHOW_MS } from "../../lib/emojis";
import { t } from "../../lib/i18n";

/**
 * The emoji a player sends during a game: 3 s under their picture on the others' screens, above it on
 * their own. The server only passes it on (backend/emoji_reactions.py); nothing is kept.
 */

export function useEmojiReactions(roomId, myId) {
  const [shown, setShown] = useState({}); // user_id → { emoji, key }
  const timers = useRef({});
  const nextKey = useRef(0);
  const myIdRef = useRef(myId);
  myIdRef.current = myId;

  const hide = useCallback((userId) => {
    clearTimeout(timers.current[userId]);
    setShown((prev) => {
      const { [userId]: _gone, ...rest } = prev;
      return rest;
    });
  }, []);

  const show = useCallback((userId, emoji) => {
    clearTimeout(timers.current[userId]);
    nextKey.current += 1;
    const key = nextKey.current;
    setShown((prev) => ({ ...prev, [userId]: { emoji, key } }));
    timers.current[userId] = setTimeout(() => hide(userId), EMOJI_SHOW_MS);
  }, [hide]);

  useEffect(() => {
    const all = timers.current;
    return () => Object.values(all).forEach(clearTimeout);
  }, []);

  // From the room's socket. The server sends it to everybody: mine is on my screen already.
  const receive = useCallback(({ user_id: userId, emoji }) => {
    if (userId !== myIdRef.current && EMOJIS.includes(emoji)) show(userId, emoji);
  }, [show]);

  // Shown at once; it goes if the server did not pass it on, as the others never saw it.
  const send = useCallback((emoji) => {
    const me = myIdRef.current;
    show(me, emoji);
    api.post(`/rooms/${roomId}/emoji`, { emoji }).catch(() => hide(me));
  }, [roomId, show, hide]);

  return { shown, receive, send };
}

/** The button of the player's bar and its list. Off while the player's last emoji is on screen. */
export const EmojiPicker = ({ onPick, disabled }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={t("bottom.emoji")}
          aria-label={t("bottom.emoji")}
          disabled={disabled}
          data-testid="emoji-btn"
          className={`shrink-0 h-[34px] w-[34px] flex items-center justify-center rounded-2xl bg-black/40 backdrop-blur-2xl border transition-colors duration-150 disabled:opacity-40 ${
            open ? "border-amber-400/55 text-amber-300" : "border-white/8 text-white/55 hover:text-amber-300"
          }`}
          style={{ boxShadow: "0 2px 10px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)" }}
        >
          <Smile className="h-[18px] w-[18px]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        sideOffset={8}
        collisionPadding={8}
        // No focus ring on the first emoji, nor on the button once closed.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="w-auto p-1.5 rounded-2xl bg-slate-950 border-white/10 grid grid-cols-6 gap-0.5"
        data-testid="emoji-list"
      >
        {EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => { setOpen(false); onPick(emoji); }}
            data-testid={`emoji-pick-${emoji}`}
            className="h-10 w-10 flex items-center justify-center rounded-xl text-[24px] leading-none hover:bg-white/10 active:scale-90 transition-transform duration-100"
          >
            {emoji}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const GAP = 8; // from the picture to the tip of the bubble's tail

// Drawn on <body>, placed from the picture (id `player-icon-<user_id>`): the opponents' row scrolls, so it would
// clip anything hanging under a pill. It follows the picture while on screen: the player's bar slides up and down
// with the redeal vote. Only a real move writes the style (PostHog's recorder copies every change).
const Bubble = ({ userId, emoji, above }) => {
  const big = useDesktop();
  const reduceMotion = useReducedMotion();
  const anchor = useRef(null);

  useLayoutEffect(() => {
    let frame;
    let placed = "";
    const place = () => {
      const picture = document.getElementById(`player-icon-${userId}`);
      if (anchor.current) {
        const r = picture?.getBoundingClientRect();
        const at = r ? `translate(${Math.round(r.left + r.width / 2)}px, ${Math.round(above ? r.top - GAP : r.bottom + GAP)}px)` : "";
        if (at !== placed) {
          placed = at;
          anchor.current.style.transform = at;
          anchor.current.style.visibility = at ? "visible" : "hidden";
        }
      }
      frame = requestAnimationFrame(place);
    };
    place();
    return () => cancelAnimationFrame(frame);
  }, [userId, above]);

  return (
    // z-45: over the player's bar and the vote's dim light (z-40), under the end-of-round windows (z-50).
    <div ref={anchor} className="fixed left-0 top-0 z-[45] pointer-events-none" style={{ visibility: "hidden" }} aria-hidden="true" data-testid={`emoji-bubble-${userId}`}>
      <div className={`absolute left-0 -translate-x-1/2 ${above ? "bottom-0" : "top-0"}`}>
        <motion.div
          className={`relative flex items-center justify-center rounded-2xl bg-slate-950 border border-amber-300/50 shadow-[0_6px_18px_rgba(0,0,0,0.55)] ${big ? "h-14 w-14 text-[34px]" : "h-11 w-11 text-[26px]"}`}
          style={{ transformOrigin: above ? "bottom center" : "top center" }}
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.4, y: above ? 10 : -10 }}
          animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, ...(reduceMotion ? {} : { scale: 0.85 }), transition: { duration: 0.15 } }}
          transition={{ type: "spring", stiffness: 520, damping: 26 }}
        >
          <span className="leading-none">{emoji}</span>
          <span
            className={`absolute left-1/2 -translate-x-1/2 rotate-45 h-2.5 w-2.5 bg-slate-950 border-amber-300/50 ${
              above ? "-bottom-[6px] border-r border-b" : "-top-[6px] border-l border-t"
            }`}
          />
        </motion.div>
      </div>
    </div>
  );
};

export const EmojiLayer = ({ shown, myId }) => createPortal(
  <AnimatePresence>
    {Object.entries(shown).map(([userId, { emoji, key }]) => (
      <Bubble key={key} userId={userId} emoji={emoji} above={userId === myId} />
    ))}
  </AnimatePresence>,
  document.body,
);
